import React from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { keyframes } from "@mui/material/styles";
import tokens from "@/theme/tokens";
import radii from "@/theme/radii";
import { DISPLAY_FONT } from "@/theme/Typography";

const planetRotate = keyframes`
  to { transform: rotate(1turn); }
`;

// One starfield, drawn as a box-shadow point list on the ::before/::after
// pseudo-elements of a single 0.5vmin dot, position and color rolled fresh
// per mount rather than the fixed lists this used to be (white/blue on
// ::before, red on ::after) — vendor-prefixed copies of the old fixed lists
// were dropped when this moved off Sass — emotion prefixes for us.
const STAR_COLORS = [
  { blur: tokens.shadow.starGlowWhiteBlur, color: tokens.color.starGlowWhite },
  { blur: tokens.shadow.starGlowWhiteBlur, color: tokens.color.starGlowBlue },
  { blur: tokens.shadow.starGlowRedBlur, color: tokens.color.starGlowRed },
];

/**
 * A tiny seeded PRNG rather than bare `Math.random()`: the splash should look
 * different on every real page load, but `styleFingerprint.test.tsx` freezes
 * `Date` to one instant for its whole run and hashes the emotion class names
 * a route emits — an unseeded random box-shadow would give that class a new
 * content hash on every run and permanently break the 9 bare-spinner cases
 * it covers. Seeding from `Date.now()` gets both for free: real wall-clock
 * time differs on every production load, and that test file's frozen clock
 * makes it return the same instant call after call.
 */
const mulberry32 = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const randomStarfield = (rand: () => number, count: number) =>
  Array.from({ length: count }, () => {
    const { blur, color } =
      STAR_COLORS[Math.floor(rand() * STAR_COLORS.length)];
    return `${(rand() * 100).toFixed(2)}vw ${(rand() * 100).toFixed(2)}vh ${blur} ${color}`;
  }).join(", ");

/**
 * Cached at module scope, not component scope — a `useMemo(..., [])` inside
 * `LoadingScreen` only survives re-renders of one mounted instance, not an
 * actual remount, and this remounts more than the "one splash per load" name
 * suggests: `<Suspense fallback={<Loading />}>` re-suspending tears the
 * fallback down and back up, and React 18 `StrictMode` (`src/index.tsx`)
 * double-mounts every component on its first mount in dev. Either one
 * re-ran the memo's factory and reseeded the sky mid-load, which is the bug
 * this was reported for.
 *
 * Deliberately lazy rather than computed at import time: reading `Date.now()`
 * as soon as the module evaluates would run before
 * `styleFingerprint.test.tsx`'s `beforeAll` freezes `Date`, since ES module
 * evaluation happens at import time, ahead of any test hook. Reading it on
 * first *call* instead means the first real mount — which in that suite
 * happens inside an `it`, after the freeze — is what sets the seed, and
 * every mount after that, in a real page load or a test run alike, reuses
 * the same cached pair.
 */
let cachedStarfields: [string, string] | null = null;
const starfields = (): [string, string] => {
  if (!cachedStarfields) {
    const rand = mulberry32(Date.now());
    // 41/20 split matches the old fixed lists' density (31 on ::before
    // including the lone extra blue star, 20 red on ::after).
    cachedStarfields = [randomStarfield(rand, 41), randomStarfield(rand, 20)];
  }
  return cachedStarfields;
};

const star = {
  content: '""',
  position: "absolute",
  top: 0,
  left: 0,
  display: "block",
  width: ".5vmin",
  height: ".5vmin",
  borderRadius: radii.circle,
  color: "transparent",
} as const;

const LoadingScreen = () => {
  const [beforeStars, afterStars] = starfields();

  return (
    <Box
      sx={{
        position: "fixed",
        zIndex: 999,
        opacity: 0.99,
        top: 0,
        left: 0,
        width: "100vw",
        height: "100%",
      }}
    >
      <Box
        sx={{
          m: 0,
          height: "100%",
          minHeight: "100vh",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          pt: "50vh",
          bgcolor: "neutral.dark",
          color: "grey.400",
          "&::before": { ...star, boxShadow: beforeStars },
          "&::after": { ...star, boxShadow: afterStars },
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            height: "12vmin",
            width: "120vmin",
            display: "flex",
            justifyContent: "center",
            position: "relative",
            // !important: MuiCssBaseline's global `h2:not(.MuiTypography-root)`
            // reset (Override.tsx) targets this element too — a Box rendered
            // as an <h2> carries `.MuiBox-root`, never `.MuiTypography-root`
            // — and that selector's specificity beats this class regardless
            // of source order, repainting the word the same teal as the
            // backdrop.
            // Resolved to the literal value first: sx's dotted-path color
            // lookup ("grey.400") only matches the bare string, so appending
            // "!important" to it breaks the lookup and falls through to
            // invalid literal CSS instead of overriding anything.
            color: (theme) => `${theme.palette.grey[400]} !important`,
            "& em": (theme) => ({
              // One-off override, not a shared type-ramp variant: Outfit at
              // 20px/500 — Typography.tsx documents Outfit as display-only,
              // always weight 800, never at 19/20/17px, so this deliberately
              // steps outside that ramp rather than reusing h3/subtitle1.
              fontFamily: DISPLAY_FONT,
              fontSize: theme.typography.pxToRem(20),
              fontWeight: 500,
              color: "currentColor",
              position: "relative",
              fontStyle: "normal",
              textAlign: "center",
            }),
          }}
        >
          <em>L</em>
          <Box
            component="em"
            sx={{
              animation: `${planetRotate} 4s linear infinite`,
              position: "relative",
              transformOrigin: "50vmin 5vmin",
              // !important, same reason as the h2's own color above: this
              // element is still an <em>, so the h2's own "& em" descendant
              // rule (color: currentColor) — one class plus one element,
              // (0,1,1) — beats this Box's single own class (0,1,0)
              // regardless of source order. Resolved to the literal value
              // first for the same reason too: the dotted path
              // ("accent.main !important") isn't a path sx's lookup matches,
              // so it'd fall through to invalid literal CSS instead.
              color: (theme) => `${theme.palette.accent.main} !important`,
            }}
          >
            O
          </Box>
          <em>A</em>
          <em>D</em>
          <em>I</em>
          <em>N</em>
          <em>G</em>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Full-screen splash shown until the app's bootstrap data has landed.
 * Renders nothing once loaded, or if the token was rejected — in that case
 * the router sends the user to /auth/login and the splash would cover it.
 */
const Loading = () => {
  // Selected as two booleans rather than one object: useSelector compares by
  // reference, so returning a fresh object here would re-render on every
  // dispatched action. connect() shallow-compared, so this keeps the old
  // render behaviour.
  const isTokenError = useSelector((state: any) =>
    Boolean(state.metadata.loading.get("tokenError"))
  );
  const isLoaded = useSelector((state: any) => {
    if (!state.user.get("jwt")) return true;
    const loading = state.metadata.loading;
    return Boolean(
      loading.get("appDataLoaded") &&
      loading.get("profileDataLoaded") &&
      loading.get("challengeDataLoaded") &&
      loading.get("projectsLoaded") &&
      loading.get("projectDataLoaded") &&
      loading.get("hubsLoaded") &&
      loading.get("userLoaded")
    );
  });

  if (isLoaded || isTokenError) return null;
  return <LoadingScreen />;
};

export default Loading;
